import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
} from 'design-system'
import { useAutoUpdater } from '../../hooks/useAutoUpdater'
import type { UpdateProgress } from '../../hooks/useAutoUpdater'
import {
  RefreshCw,
  Download,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ArrowDownToLine,
} from 'lucide-react'
import { cn } from 'design-system'
import { semanticKpiIconClass, semanticSolidSurfaceClass } from 'helpers/kpiIconClass'

// ── Helpers ────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

function formatSpeed(bytesPerSecond: number): string {
  return formatBytes(bytesPerSecond) + '/s'
}

// ── Progress Bar ───────────────────────────────────────────────────────────

function ProgressBar({ progress }: { progress: UpdateProgress }) {
  const percent = Math.round(progress.percent)

  return (
    <div className="space-y-3">
      {/* Bar */}
      <div className="relative h-3 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
        <div
          className="h-full rounded-full bg-info transition-all duration-500 ease-out"
          style={{ width: `${percent}%` }}
        />
        {/* Shimmer animation */}
        <div
          className="absolute inset-0 h-full rounded-full bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"
          style={{ width: `${percent}%` }}
        />
      </div>

      {/* Stats row */}
      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
        <span className="font-medium tabular-nums">{percent}%</span>
        <span className="tabular-nums">
          {formatBytes(progress.transferred)} / {formatBytes(progress.total)}
        </span>
        <span className="tabular-nums">{formatSpeed(progress.bytesPerSecond)}</span>
      </div>
    </div>
  )
}

// ── Modal Content per Status ───────────────────────────────────────────────

function CheckingContent() {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center gap-4 py-6">
      <div className="relative">
        <div className="absolute inset-0 animate-ping rounded-full bg-info/30" />
        <div className={cn('relative flex h-16 w-16 items-center justify-center rounded-full shadow-lg', semanticKpiIconClass('info'))}>
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
      <div className="text-center">
        <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          {t('common.update.checking')}
        </p>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {t('common.update.pleaseWait')}
        </p>
      </div>
    </div>
  )
}

function AvailableContent({
  version,
  onDownload,
  onDismiss,
}: {
  version: string | null
  onDownload: () => void
  onDismiss: () => void
}) {
  const { t } = useTranslation()
  return (
    <>
      <div className="flex flex-col items-center gap-4 py-4">
        <div className={cn('flex h-16 w-16 items-center justify-center rounded-full shadow-lg', semanticKpiIconClass('success'))}>
          <ArrowDownToLine className="h-8 w-8" />
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {t('common.update.availableTitle')}
          </p>
          {version && (
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t('common.update.versionAvailable', { version })}
            </p>
          )}
        </div>
      </div>
      <DialogFooter className="flex gap-2 sm:justify-center">
        <Button variant="outline" onClick={onDismiss}>
          {t('common.update.later')}
        </Button>
        <Button
          onClick={onDownload}
          className="border-0 bg-success text-success-foreground hover:bg-success/90"
        >
          <Download className="ml-2 h-4 w-4" />
          {t('common.update.now')}
        </Button>
      </DialogFooter>
    </>
  )
}

function DownloadingContent({
  progress,
  onDismiss,
}: {
  progress: UpdateProgress | null
  onDismiss: () => void
}) {
  const { t } = useTranslation()
  return (
    <>
      <div className="flex flex-col items-center gap-4 py-4">
        <div className={cn('flex h-16 w-16 items-center justify-center rounded-full shadow-lg', semanticKpiIconClass('info'))}>
          <Download className="h-8 w-8 animate-bounce" />
        </div>
        <div className="w-full text-center">
          <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {t('common.update.downloading')}
          </p>
          <p className="mb-4 mt-1 text-sm text-slate-500 dark:text-slate-400">
            {t('common.update.continueWorking')}
          </p>
          {progress && <ProgressBar progress={progress} />}
        </div>
      </div>
      <DialogFooter className="sm:justify-center">
        <Button variant="outline" onClick={onDismiss}>
          {t('common.update.hide')}
        </Button>
      </DialogFooter>
    </>
  )
}

function DownloadedContent({
  version,
  onInstall,
  onDismiss,
}: {
  version: string | null
  onInstall: () => void
  onDismiss: () => void
}) {
  const { t } = useTranslation()
  return (
    <>
      <div className="flex flex-col items-center gap-4 py-4">
        <div className="relative">
          <div className="absolute inset-0 animate-pulse rounded-full bg-success/30" />
          <div className={cn('relative flex h-16 w-16 items-center justify-center rounded-full shadow-lg', semanticKpiIconClass('success'))}>
            <CheckCircle2 className="h-8 w-8" />
          </div>
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {t('common.update.readyTitle')}
          </p>
          {version && (
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t('common.update.versionDownloaded', { version })}
            </p>
          )}
          <p className="mt-2 text-xs text-warning">
            {t('common.update.restartWarning')}
          </p>
        </div>
      </div>
      <DialogFooter className="flex gap-2 sm:justify-center">
        <Button variant="outline" onClick={onDismiss}>
          {t('common.update.later')}
        </Button>
        <Button
          onClick={onInstall}
          className="border-0 bg-success text-success-foreground hover:bg-success/90"
        >
          <RotateCcw className="ml-2 h-4 w-4" />
          {t('common.update.restartInstall')}
        </Button>
      </DialogFooter>
    </>
  )
}

function NotAvailableContent({
  version,
  onDismiss,
}: {
  version: string | null
  onDismiss: () => void
}) {
  const { t } = useTranslation()
  return (
    <>
      <div className="flex flex-col items-center gap-4 py-6">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-slate-400 to-slate-500 text-white shadow-lg shadow-slate-500/25">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {t('common.update.upToDate')}
          </p>
          {version && (
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t('common.update.currentVersion', { version })}
            </p>
          )}
        </div>
      </div>
      <DialogFooter className="sm:justify-center">
        <Button variant="outline" onClick={onDismiss}>
          {t('common.update.ok')}
        </Button>
      </DialogFooter>
    </>
  )
}

function ErrorContent({
  error,
  onRetry,
  onDismiss,
}: {
  error: string | null
  onRetry: () => void
  onDismiss: () => void
}) {
  const { t } = useTranslation()
  return (
    <>
      <div className="flex flex-col items-center gap-4 py-4">
        <div className={cn('flex h-16 w-16 items-center justify-center rounded-full shadow-lg', semanticKpiIconClass('danger'))}>
          <AlertCircle className="h-8 w-8" />
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {t('common.update.errorTitle')}
          </p>
          {error && (
            <p className={cn('mt-2 max-w-sm rounded-lg px-3 py-2 text-xs', semanticSolidSurfaceClass('danger'))} dir="ltr">
              {error}
            </p>
          )}
        </div>
      </div>
      <DialogFooter className="flex gap-2 sm:justify-center">
        <Button variant="outline" onClick={onDismiss}>
          {t('ui.close')}
        </Button>
        <Button
          onClick={onRetry}
          className="border-0 bg-info text-info-foreground hover:bg-info/90"
        >
          <RefreshCw className="ml-2 h-4 w-4" />
          {t('common.update.retry')}
        </Button>
      </DialogFooter>
    </>
  )
}

// ── Main Component ─────────────────────────────────────────────────────────

export function UpdateModal() {
  const { t } = useTranslation()
  const {
    status,
    version,
    progress,
    error,
    showModal,
    checkForUpdates,
    downloadUpdate,
    installUpdate,
    dismiss,
  } = useAutoUpdater()

  // Don't render anything when in idle state or modal is hidden
  if (status === 'idle' && !showModal) return null
  if (!showModal) return null

  const titleMap: Record<string, string> = {
    checking: t('common.update.status.checking'),
    available: t('common.update.status.available'),
    'not-available': t('common.update.status.notAvailable'),
    downloading: t('common.update.status.downloading'),
    downloaded: t('common.update.status.downloaded'),
    error: t('common.update.status.error'),
  }

  // Prevent closing during install
  const canClose = status !== 'checking'

  return (
    <Dialog
      open={showModal}
      onOpenChange={(open) => {
        if (!open && canClose) dismiss()
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">
            {titleMap[status] || t('common.update.modalTitle')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t('common.update.modalDescription')}
          </DialogDescription>
        </DialogHeader>

        {status === 'checking' && <CheckingContent />}

        {status === 'available' && (
          <AvailableContent
            version={version}
            onDownload={downloadUpdate}
            onDismiss={dismiss}
          />
        )}

        {status === 'downloading' && (
          <DownloadingContent progress={progress} onDismiss={dismiss} />
        )}

        {status === 'downloaded' && (
          <DownloadedContent
            version={version}
            onInstall={installUpdate}
            onDismiss={dismiss}
          />
        )}

        {status === 'not-available' && (
          <NotAvailableContent version={version} onDismiss={dismiss} />
        )}

        {status === 'error' && (
          <ErrorContent
            error={error}
            onRetry={checkForUpdates}
            onDismiss={dismiss}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

// ── Check for Updates Button ───────────────────────────────────────────────

/**
 * A standalone "Check for Updates" button.
 * Drop this anywhere in your settings/sidebar/about page.
 */
export function CheckForUpdatesButton({
  className = '',
}: {
  className?: string
}) {
  const { t } = useTranslation()
  const { status, appVersion, checkForUpdates } = useAutoUpdater()
  const isElectron = typeof window !== 'undefined' && !!window.electronAPI

  // Hide entirely if not running inside Electron
  if (!isElectron) return null

  const isChecking = status === 'checking'

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <Button
        variant="outline"
        size="sm"
        onClick={checkForUpdates}
        disabled={isChecking}
        className="gap-2"
      >
        {isChecking ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <RefreshCw className="h-4 w-4" />
        )}
        {isChecking ? t('common.update.checkingButton') : t('common.update.checkButton')}
      </Button>
      {appVersion && (
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {t('common.update.currentVersion', { version: appVersion })}
        </span>
      )}
    </div>
  )
}

export default UpdateModal
